import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  TextField,
  Button,
  Container,
  Box,
  Popover,
  ClickAwayListener,
} from '@mui/material';

interface Props {
  name?: string;
  value?: string;
  label?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onError?: string;
  error?: boolean;
  helperText?: string;
  variant?: 'standard' | 'outlined';
  margin?: 'normal' | 'dense';
  fullWidth?: boolean;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}

const TextFieldKeyboard: React.FC<Props> = (props) => {
  const { label, helperText, variant, fullWidth, placeholder, multiline, rows } = props;
  const [value, setValue] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isShiftActive, setIsShiftActive] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textFieldRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Фикс: Record<string, string> для индексного доступа
  const shiftSymbolMap: Record<string, string> = useMemo(
    () => ({
      '`': '~',
      '1': '!',
      '2': '"',
      '3': '№',
      '4': ';',
      '5': '%',
      '6': ':',
      '7': '?',
      '8': '*',
      '9': '(',
      '0': ')',
      '-': '_',
      '=': '+',
    }),
    []
  );

  const cyrillicRows = useMemo(
    () => [
      ['/', 'й', 'ц', 'у', 'к', 'е', 'н', 'г', 'ш', 'щ', 'з', 'х', 'ъ', '\\'],
      ['ф', 'ы', 'в', 'а', 'п', 'р', 'о', 'л', 'д', 'ж', 'э'],
      ['<', '>', 'я', 'ч', 'с', 'м', 'и', 'т', 'ь', 'б', 'ю', '.', ','],
    ],
    []
  );

  const baseSymbolRow = useMemo(
    () => ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='],
    []
  );

  // Оптимизированная прокрутка
  const scrollToEnd = useCallback(() => {
    if (inputRef.current) {
      const textarea = inputRef.current.querySelector('textarea');
      if (textarea) {
        requestAnimationFrame(() => {
          textarea.scrollTop = textarea.scrollHeight;
        });
      }
    }
  }, []);

  // Используем useLayoutEffect или useEffect с проверкой изменения
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (prevValueRef.current !== value) {
      scrollToEnd();
      prevValueRef.current = value;
    }
  }, [value, scrollToEnd]);

  // Мемоизация отображаемых строк
  const displaySymbolRow = useMemo(
    () =>
      baseSymbolRow.map((char) => {
        const display = isShiftActive && shiftSymbolMap[char] ? shiftSymbolMap[char] : char;
        return { original: char, display };
      }),
    [isShiftActive, baseSymbolRow, shiftSymbolMap]
  );

  const displayCyrillicRows = useMemo(
    () =>
      cyrillicRows.map((row) =>
        row.map((char) => {
          const isRussian = /[а-яА-ЯёЁ]/.test(char);
          const isEnglish = /[a-zA-Z]/.test(char);
          if (isShiftActive && (isRussian || isEnglish)) {
            return char.toUpperCase();
          }
          return char;
        })
      ),
    [isShiftActive, cyrillicRows]
  );

  // Обработчик изменения в поле ввода
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value.replace(/[^a-zA-Zа-яА-ЯёЁ0-9\s.,!?\-]/g, '');
      setValue(newValue);
      if (props.onChange) {
        props.onChange(event);
      }
    },
    [props.onChange]
  );

  // Обработчик клика по кнопке клавиатуры
  const handleKeyboardClick = useCallback(
    (char: string) => {
      let newValue: string;

      switch (char) {
        case '✖':
          newValue = '';
          break;
        case '⌫':
          newValue = value.slice(0, -1);
          break;
        case '␣':
          newValue = value + ' ';
          break;
        case '⇧':
          setIsShiftActive((prev) => !prev);
          return;
        case '↵':
          newValue = value + '\n';
          break;
        default: {
          // Проверяем, является ли символ специальным для Shift
          const shiftChar = isShiftActive && shiftSymbolMap[char] ? shiftSymbolMap[char] : null;
          const insertChar = shiftChar || (isShiftActive ? char.toUpperCase() : char);
          newValue = value + insertChar;
        }
      }

      setValue(newValue);
      if (props.onChange) {
        props.onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [value, isShiftActive, shiftSymbolMap, props.onChange]
  );

  // Обработчики открытия/закрытия
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      setAnchorEl(event.currentTarget);
      // Небольшая задержка для прокрутки
      setTimeout(scrollToEnd, 10);
    },
    [scrollToEnd]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setIsShiftActive(false);
  }, []);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (inputRef.current && inputRef.current.contains(event.target as Node)) {
        return;
      }
      handleClose();
    },
    [handleClose]
  );

  const open = Boolean(anchorEl);
  const id = open ? 'keyboard-popover' : undefined;

  // Рендер клавиатуры (мемоизирован)
  const keyboard = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Строка символов */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {displaySymbolRow.map(({ original, display }) => (
            <Button
              key={original}
              variant="outlined"
              size="small"
              disableRipple={true}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleKeyboardClick(original)}
              sx={{
                fontSize: '0.9rem',
                minWidth: '48px',
                height: '48px',
                padding: '2px',
                flex: '0 0 auto',
              }}
            >
              {display}
            </Button>
          ))}
          <Button
            variant="outlined"
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleKeyboardClick('⌫')}
            sx={{
              fontSize: '1.1rem',
              minWidth: '80px',
              height: '48px',
              flex: '0 0 auto',
            }}
          >
            ⌫
          </Button>
        </Box>

        {/* Основные ряды */}
        {displayCyrillicRows.map((row, rowIndex) => {
          const showShift = rowIndex === 1;
          const showEnter = rowIndex === 1;
          return (
            <Box key={rowIndex} sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              {showShift && (
                <Button
                  variant={isShiftActive ? 'contained' : 'outlined'}
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleKeyboardClick('⇧')}
                  sx={{
                    fontSize: '1.4rem',
                    minWidth: '80px',
                    height: '48px',
                    flex: '0 0 auto',
                    backgroundColor: isShiftActive ? '#FFFFFF' : 'transparent',
                    '&:hover': {
                      backgroundColor: isShiftActive ? '#FFFFFF' : 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  ⇧
                </Button>
              )}
              {row.map((displayChar, index) => {
                const originalChar = cyrillicRows[rowIndex][index];
                return (
                  <Button
                    key={originalChar}
                    variant="contained"
                    size="small"
                    disableRipple={true}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleKeyboardClick(originalChar)}
                    sx={{
                      fontSize: '1.1rem',
                      minWidth: '48px',
                      height: '48px',
                      padding: '2px',
                      flex: '0 0 auto',
                    }}
                  >
                    {displayChar}
                  </Button>
                );
              })}
              {showEnter && (
                <Button
                  variant="outlined"
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleKeyboardClick('↵')}
                  sx={{
                    fontSize: '1.4rem',
                    minWidth: '80px',
                    height: '48px',
                    flex: '0 0 auto',
                  }}
                >
                  ↵
                </Button>
              )}
            </Box>
          );
        })}

        {/* Нижние кнопки */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleKeyboardClick('✖')}
            sx={{
              height: '48px',
              flex: '0 0 auto',
              px: 2,
              backgroundColor: '#fa3d3d',
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Очистить
          </Button>
          <Button
            variant="contained"
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleKeyboardClick('␣')}
            sx={{ height: '48px', flex: '0 0 auto', px: 3, minWidth: '300px' }}
          >
            Пробел
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleClose}
            sx={{ height: '48px', flex: '0 0 auto', px: 2, backgroundColor: '#00ff666e' }}
          >
            Сохранить
          </Button>
        </Box>
      </Box>
    ),
    [displaySymbolRow, displayCyrillicRows, isShiftActive, handleKeyboardClick, handleClose]
  );

  return (
    <Container maxWidth="xl" disableGutters>
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div ref={textFieldRef}>
          <TextField
            ref={inputRef}
            label={label}
            variant={variant || 'standard'}
            fullWidth={fullWidth !== undefined ? fullWidth : true}
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            placeholder={placeholder}
            multiline={multiline}
            rows={rows}
            InputProps={{
              inputMode: 'text',
            }}
            helperText={helperText}
            error={props.error}
            sx={{
              '& .MuiInputBase-root': {
                alignItems: 'flex-start',
              },
              '& textarea': {
                scrollBehavior: 'smooth',
              },
            }}
          />
        </div>

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              p: 2,
              maxWidth: '800px',
              width: '100%',
              bgcolor: 'background.paper',
              display: 'flex',
              justifyContent: 'center',
            },
            ref: popoverRef,
          }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          closeAfterTransition={false}
        >
          <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown" touchEvent="onTouchStart">
            {keyboard}
          </ClickAwayListener>
        </Popover>
      </Box>
    </Container>
  );
};

export default React.memo(TextFieldKeyboard);